/**
 * Transfer In List Component
 * Transfer In records are auto-created at the destination location when a Goods Issue
 * Note is approved (stock dispatched to the in-transit ledger). From here the
 * destination confirms the actual received quantity per line and approves (which
 * credits destination stock) or rejects (which reverses the dispatch back to source).
 */

import React, { useState, useEffect, useRef } from 'react';
import { useInventory } from '../../../contexts/InventoryContext';
import LoadingSpinner from '../../Common/LoadingSpinner';
import TextInput from '../../Common/TextInput';
import CustomSelect from '../../Common/CustomSelect';
import PermissionGuard from '../../Common/PermissionGuard';
import ConfirmationModal from '../../Common/ConfirmationModal';
import TransferInForm from './TransferInForm';
import toast from 'react-hot-toast';

const TransferInList = () => {
    const {
        transferIns,
        loading,
        fetchTransferIns,
        approveTransferIn,
        rejectTransferIn
    } = useInventory();
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearchInput, setDebouncedSearchInput] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [error, setError] = useState('');
    const [selectedTI, setSelectedTI] = useState(null);
    const [editingTI, setEditingTI] = useState(null);
    const [confirmAction, setConfirmAction] = useState(null);
    const searchTimeoutRef = useRef(null);

    const [view, setView] = useState(() => {
        try {
            return localStorage.getItem('transferInListView') || 'card';
        } catch {
            return 'card';
        }
    });

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
    });

    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchInput(searchInput);
            setPagination(prev => ({ ...prev, page: 1 }));
        }, 500);
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, [searchInput]);

    useEffect(() => {
        loadTransferIns();
    }, [debouncedSearchInput, statusFilter, pagination.page, pagination.limit]);

    const loadTransferIns = async () => {
        try {
            const params = { page: pagination.page, limit: pagination.limit };
            if (debouncedSearchInput) params.search = debouncedSearchInput;
            if (statusFilter) params.status = statusFilter;

            const result = await fetchTransferIns(params);

            if (result?.pagination) {
                setPagination({
                    page: result.pagination.page || 1,
                    limit: result.pagination.limit || 20,
                    total: result.pagination.total || 0,
                    pages: result.pagination.pages || 0
                });
            }
            setError('');
        } catch (err) {
            console.error('Error loading transfer-ins:', err);
            setError(err.response?.data?.message || err.message || 'Failed to load transfer-ins');
        }
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleLimitChange = (newLimit) => {
        setPagination(prev => ({ page: 1, limit: newLimit, total: prev.total, pages: prev.pages }));
    };

    const handleViewChange = (newView) => {
        setView(newView);
        try {
            localStorage.setItem('transferInListView', newView);
        } catch (error) {
            console.warn('Failed to save view preference:', error);
        }
    };

    const clearFilters = () => {
        setSearchInput('');
        setStatusFilter('');
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const hasFilters = () => {
        return searchInput || statusFilter;
    };

    const hasDiscrepancies = (ti) => (ti.lineItems || []).some(line => (line.shortQuantity || 0) > 0);

    const handleApprove = (ti, payload) => {
        setConfirmAction({
            type: 'success',
            title: 'Approve Transfer In',
            message: `Approve "${ti.transferInNo}"? This credits your warehouse's stock for the received quantities entered.`,
            confirmText: 'Approve & Receive',
            onConfirm: async () => {
                try {
                    await approveTransferIn(ti._id, payload || {});
                    toast.success('Transfer-in approved — stock received');
                    setConfirmAction(null);
                    setEditingTI(null);
                    loadTransferIns();
                } catch (err) {
                    toast.error(err.response?.data?.message || 'Failed to approve transfer-in');
                }
            }
        });
    };

    const handleReject = (ti) => {
        setConfirmAction({
            type: 'danger',
            title: 'Reject Transfer In',
            message: `Are you sure you want to reject "${ti.transferInNo}"? Stock will be returned to the source warehouse. Please provide a reason.`,
            confirmText: 'Reject',
            hasInput: true,
            inputLabel: 'Rejection Reason',
            inputRequired: true,
            onConfirm: async (reason) => {
                try {
                    await rejectTransferIn(ti._id, reason);
                    toast.success('Transfer-in rejected — stock returned to source');
                    setConfirmAction(null);
                    loadTransferIns();
                } catch (err) {
                    toast.error(err.response?.data?.message || 'Failed to reject transfer-in');
                }
            }
        });
    };

    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'pending':
                return 'bg-orange-100 text-orange-700 border-orange-300';
            case 'approved':
                return 'bg-emerald-100 text-emerald-700 border-emerald-300';
            case 'rejected':
                return 'bg-red-100 text-red-700 border-red-300';
            default:
                return 'bg-gray-100 text-gray-600 border-gray-300';
        }
    };

    const statusOptions = [
        { value: '', label: 'All Statuses' },
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' }
    ];

    const items = transferIns || [];

    if (loading && !transferIns) {
        return <LoadingSpinner />;
    }

    const DiscrepancyBadge = ({ ti }) =>
        hasDiscrepancies(ti) ? (
            <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border bg-amber-100 text-amber-700 border-amber-300 flex-shrink-0"
                title="One or more lines arrived short of the issued quantity"
            >
                Short
            </span>
        ) : null;

    const renderTICard = (ti) => (
        <div key={ti._id} className="bg-white border border-gray-200 rounded shadow-sm hover:shadow-md transition-all duration-200 group w-full">
            <div className="p-3 flex flex-col gap-2">

                {/* Header */}
                <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                            {ti.transferInNo?.slice(-2) || 'TI'}
                        </div>
                        <div className="min-w-0 flex-1">
                            <button
                                onClick={() => setSelectedTI(ti)}
                                className="text-sm font-semibold text-gray-900 hover:text-indigo-600 transition-colors truncate text-left block"
                            >
                                {ti.transferInNo}
                            </button>
                            <p className="text-xs text-gray-400">{new Date(ti.createdAt).toLocaleDateString()}</p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <button
                            onClick={() => setSelectedTI(ti)}
                            className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                            title="View"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                        </button>
                        {ti.status === 'pending' && (
                            <PermissionGuard permission="INVENTORY:TRANSFER_IN:UPDATE">
                                <button
                                    onClick={() => setEditingTI(ti)}
                                    className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                                    title="Receive / Edit"
                                >
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                </button>
                            </PermissionGuard>
                        )}
                        {ti.status === 'pending' && (
                            <PermissionGuard permission="INVENTORY:TRANSFER_IN:APPROVE">
                                <button
                                    onClick={() => handleApprove(ti)}
                                    className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-all"
                                    title="Approve"
                                >
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => handleReject(ti)}
                                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                                    title="Reject"
                                >
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </PermissionGuard>
                        )}
                    </div>
                </div>

                {/* Route row */}
                <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="truncate">{ti.sourceWarehouseName}</span>
                    <svg className="w-3 h-3 mx-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="truncate">{ti.destinationWarehouseName}</span>
                </div>

                {/* Items + Status */}
                <div className="flex items-center justify-between pt-1 border-t border-gray-100">
                    <span className="text-xs text-gray-600 truncate">
                        {ti.lineItems?.length || 0} items • {ti.gtrRequestNo}
                    </span>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                        <DiscrepancyBadge ti={ti} />
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${getStatusBadgeColor(ti.status)}`}>
                            {ti.status}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderTIRow = (ti) => (
        <tr key={ti._id} className="hover:bg-gray-50 transition-colors">
            <td className="px-4 py-3 whitespace-nowrap">
                <div className="flex items-center space-x-3">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-xs font-semibold text-indigo-700 flex-shrink-0">
                        {ti.transferInNo?.slice(-2) || 'TI'}
                    </div>
                    <div>
                        <button
                            onClick={() => setSelectedTI(ti)}
                            className="text-sm font-semibold text-gray-900 hover:text-indigo-600"
                        >
                            {ti.transferInNo}
                        </button>
                        <div className="text-xs text-gray-400">
                            {new Date(ti.createdAt).toLocaleDateString()}
                        </div>
                    </div>
                </div>
            </td>
            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{ti.gtrRequestNo}</td>
            <td className="px-4 py-3 whitespace-nowrap">
                <div className="text-sm text-gray-900">{ti.sourceWarehouseName}</div>
                <div className="text-sm text-gray-500">→ {ti.destinationWarehouseName}</div>
            </td>
            <td className="px-4 py-3 whitespace-nowrap">
                <div className="flex items-center gap-1">
                    <DiscrepancyBadge ti={ti} />
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${getStatusBadgeColor(ti.status)}`}>
                        {ti.status}
                    </span>
                </div>
            </td>
            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                {ti.lineItems?.length || 0} items
            </td>
            <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end gap-2">
                    <button
                        onClick={() => setSelectedTI(ti)}
                        className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-all"
                        title="View"
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                    </button>
                    {ti.status === 'pending' && (
                        <>
                            <PermissionGuard permission="INVENTORY:TRANSFER_IN:UPDATE">
                                <button
                                    onClick={() => setEditingTI(ti)}
                                    className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-all"
                                    title="Receive / Edit"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                </button>
                            </PermissionGuard>
                            <PermissionGuard permission="INVENTORY:TRANSFER_IN:APPROVE">
                                <button
                                    onClick={() => handleApprove(ti)}
                                    className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded transition-all"
                                    title="Approve"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => handleReject(ti)}
                                    className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-all"
                                    title="Reject"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </PermissionGuard>
                        </>
                    )}
                </div>
            </td>
        </tr>
    );

    return (
        <div className="bg-white shadow-sm border border-gray-200">
            {error && (
                <div className="mx-4 mt-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded p-3">
                    <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
            )}

            {/* Filters and Search */}
            <div className="px-4 sm:px-6 lg:px-8 py-2 border-b border-gray-200 flex-shrink-0">
                <div className="flex flex-col lg:flex-row lg:items-center gap-2">
                    {/* Search */}
                    <div className="flex-1">
                        <TextInput
                            variant="input"
                            type="text"
                            placeholder="Search by transfer-in number, GTR number, items..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            icon={({ className }) => (
                                <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            )}
                            className="text-sm py-1.5 !bg-white border border-gray-300 shadow-sm !rounded-sm"
                        />
                    </div>

                    {/* Filters and View Toggle */}
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                        <CustomSelect
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            options={statusOptions}
                            size="sm"
                            variant="default"
                            className="min-w-[150px]"
                        />

                        {/* Reset Filters */}
                        {hasFilters() && (
                            <button
                                onClick={clearFilters}
                                className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                                title="Clear all filters"
                            >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}

                        {/* View Toggle */}
                        <div className="flex bg-gray-100 rounded-md border border-gray-200 p-0.5">
                            <button
                                onClick={() => handleViewChange('card')}
                                className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'card' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                    }`}
                                title="Card view"
                            >
                                <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-7H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z" />
                                </svg>
                                Cards
                            </button>
                            <button
                                onClick={() => handleViewChange('table')}
                                className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'table' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                    }`}
                                title="List view"
                            >
                                <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                                </svg>
                                List
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pagination */}
            {pagination.total > 0 && (
                <div className="flex items-center justify-between px-2 py-2 bg-gradient-to-r from-gray-100 to-gray-100 border-b border-gray-200">
                    {/* Results Info */}
                    <div className="flex items-center gap-2">
                        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-1.5 rounded-lg shadow-sm">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                            </svg>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-gray-900">
                                {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} transfer-ins
                            </p>
                            <p className="text-[10px] text-gray-500">
                                {hasFilters() ? 'Filtered results' : 'All transfer-ins'}
                            </p>
                        </div>
                    </div>

                    {/* Navigation Controls */}
                    <div className="flex items-center gap-3">
                        {/* Items per page */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600 font-medium">Show:</span>
                            <CustomSelect
                                value={pagination.limit}
                                onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                                disabled={loading}
                                options={[
                                    { value: 10, label: '10' },
                                    { value: 20, label: '20' },
                                    { value: 50, label: '50' },
                                    { value: 100, label: '100' }
                                ]}
                                size="sm"
                                variant="minimal"
                                className="min-w-[70px]"
                            />
                        </div>

                        {/* Navigation Buttons */}
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => handlePageChange(1)}
                                disabled={pagination.page <= 1 || loading}
                                className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                title="First page"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                                </svg>
                            </button>

                            <button
                                onClick={() => handlePageChange(Math.max(1, pagination.page - 1))}
                                disabled={pagination.page <= 1 || loading}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-xs"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                <span className="hidden sm:inline">Previous</span>
                            </button>

                            {/* Page Numbers */}
                            <div className="hidden md:flex items-center gap-1">
                                {Array.from({ length: Math.min(7, pagination.pages) }, (_, i) => {
                                    const pageNum = i + 1;
                                    const isCurrentPage = pageNum === pagination.page;

                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => handlePageChange(pageNum)}
                                            disabled={loading}
                                            className={`min-w-[28px] px-2 py-1.5 rounded text-xs font-medium transition-all ${isCurrentPage
                                                ? 'bg-indigo-600 text-white shadow-sm'
                                                : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                                                }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                                {pagination.pages > 7 && (
                                    <span className="px-2 text-gray-400 text-xs">...</span>
                                )}
                            </div>

                            {/* Mobile page indicator */}
                            <div className="flex md:hidden px-3 py-1.5 bg-white rounded border border-gray-200">
                                <span className="text-xs font-medium text-gray-900">{pagination.page}</span>
                                <span className="text-xs text-gray-500 mx-1">of</span>
                                <span className="text-xs font-medium text-gray-900">{pagination.pages}</span>
                            </div>

                            <button
                                onClick={() => handlePageChange(Math.min(pagination.pages, pagination.page + 1))}
                                disabled={pagination.page >= pagination.pages || loading}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-xs"
                            >
                                <span className="hidden sm:inline">Next</span>
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>

                            <button
                                onClick={() => handlePageChange(pagination.pages)}
                                disabled={pagination.page >= pagination.pages || loading}
                                className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                title="Last page"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="p-2">
                {items.length === 0 ? (
                    <div className="text-center py-12">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No transfer-ins found</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {hasFilters() ? 'Try adjusting your search or filters.' : 'Transfer-ins appear here once a Goods Issue Note is approved at the source location.'}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Card View */}
                        {view === 'card' && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {items.map(renderTICard)}
                            </div>
                        )}

                        {/* List View */}
                        {view === 'table' && (
                            <div className="overflow-x-auto">
                                <table className="min-w-max w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Transfer In</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">GTR</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Route</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Items</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {items.map(renderTIRow)}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Simple view modal */}
            {selectedTI && (
                <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
                    <div className="flex items-center justify-center min-h-full px-4 py-6">
                        <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
                            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg px-4 py-2.5 flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-white">{selectedTI.transferInNo}</h3>
                                <button
                                    onClick={() => setSelectedTI(null)}
                                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20"
                                >
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div className="px-4 py-3">
                                <div className="grid grid-cols-2 gap-2 mb-3 text-xs text-gray-600">
                                    <div><span className="font-medium text-gray-500">GTR: </span>{selectedTI.gtrRequestNo}</div>
                                    <div><span className="font-medium text-gray-500">Status: </span>{selectedTI.status}</div>
                                    <div><span className="font-medium text-gray-500">Source: </span>{selectedTI.sourceWarehouseName}</div>
                                    <div><span className="font-medium text-gray-500">Destination: </span>{selectedTI.destinationWarehouseName}</div>
                                    {selectedTI.status === 'rejected' && selectedTI.rejectionReason && (
                                        <div className="col-span-2 text-red-600">
                                            <span className="font-medium">Rejection reason: </span>{selectedTI.rejectionReason}
                                        </div>
                                    )}
                                </div>
                                <div className="border border-gray-200 rounded-lg overflow-hidden">
                                    <table className="min-w-max w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Item</th>
                                                <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Issued</th>
                                                <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Received</th>
                                                <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Short</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-100">
                                            {(selectedTI.lineItems || []).map((line, index) => (
                                                <tr key={index}>
                                                    <td className="px-2 py-1.5">
                                                        <div className="text-xs font-medium text-gray-900">{line.itemDescription || line.assetName}</div>
                                                        <div className="text-xs text-gray-500 font-mono">{line.itemCode || line.assetCode}</div>
                                                    </td>
                                                    <td className="px-2 py-1.5 text-center text-xs text-gray-600">{line.quantityIssued}</td>
                                                    <td className="px-2 py-1.5 text-center text-xs font-medium text-gray-900">{line.quantityReceived}</td>
                                                    <td className="px-2 py-1.5 text-center text-xs font-medium text-red-600">{line.shortQuantity || 0}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit / Receive Form (pending only) */}
            {editingTI && (
                <TransferInForm
                    transferIn={editingTI}
                    onClose={() => setEditingTI(null)}
                    onSuccess={loadTransferIns}
                    onApprove={handleApprove}
                />
            )}

            {/* Confirmation Modal */}
            {confirmAction && (
                <ConfirmationModal
                    isOpen={!!confirmAction}
                    onClose={() => setConfirmAction(null)}
                    onConfirm={confirmAction.onConfirm}
                    title={confirmAction.title}
                    message={confirmAction.message}
                    confirmText={confirmAction.confirmText}
                    type={confirmAction.type}
                    hasInput={confirmAction.hasInput}
                    inputLabel={confirmAction.inputLabel}
                    inputRequired={confirmAction.inputRequired}
                />
            )}
        </div>
    );
};

export default TransferInList;
